import { Input, InputProps } from '@creditornot/cb-input';
import React, { useState, ChangeEvent, useCallback, useEffect, useRef } from 'react';

export type InputNumberProps = Omit<InputProps, 'onChange' | 'value'> & {
  onChange?: (value?: number | string) => void;
  value?: number | string;
  decimals?: number;
  maxDecimals?: number;
};

const usePrevious = <T,>(value: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const isValidNumberInput = (value: string) => !/[^\d.,-]/g.test(value) && !isNaN(Number(value.replace(',', '.')));

const valueToNumber = (value: string | number) => {
  if (typeof value === 'number') {
    return isNaN(value) ? undefined : value;
  }
  const parsedNumber = parseFloat(value.trim().replace(',', '.'));
  return isNaN(parsedNumber) ? undefined : parsedNumber;
};

const limitDecimals = (value: string, maxDecimals: number) => {
  const stringValue = value.replace(',', '.');
  if (!stringValue.includes('.')) {
    return stringValue;
  }
  if (maxDecimals === 0) {
    return stringValue.replace(/\./, '');
  }
  const captureMaxDecimalsRegex = new RegExp(`(\\d+\\.\\d{0,${maxDecimals}})\\d*`); // e.g. /(\d+\.\d{0,2})\d*/
  return stringValue.replace(captureMaxDecimalsRegex, '$1');
};

const valueToString = (value: string | number, decimals?: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: decimals, useGrouping: false });

const InputNumber = ({ onChange, value, decimals, maxDecimals, ...rest }: InputNumberProps) => {
  const [displayValue, setDisplayValue] = useState<string>('');

  const previousDecimals = usePrevious(decimals);

  useEffect(() => {
    if (value !== undefined && valueToNumber(displayValue) === valueToNumber(value) && previousDecimals === decimals) {
      return;
    }
    const valueAsString = value !== undefined ? valueToString(value, decimals) : '';
    setDisplayValue(valueAsString || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decimals, value]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!onChange) {
        return;
      }
      const { value: eventValue } = event.currentTarget;
      const valueWithMaxDecimals = maxDecimals !== undefined ? limitDecimals(eventValue, maxDecimals) : eventValue;
      if (isValidNumberInput(valueWithMaxDecimals)) {
        onChange(valueToNumber(valueWithMaxDecimals));
      } else {
        onChange(valueWithMaxDecimals);
      }
      setDisplayValue(valueWithMaxDecimals);
    },
    [onChange, maxDecimals],
  );

  return <Input {...rest} value={displayValue} onChange={handleChange} />;
};

export default InputNumber;
